import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import SupRestaurant from './SupervisorRestaurant';
import { getMenuChannel, getOrderChannel, getStoreOrderChannel } from './menuChannels';
import PlaceOrder from './PlaceOrder';
import Order from './Order';
import Purchase from './Purchase';
import Finance from './Finance';
import StorePurchase from './StorePurchase';
import UserManagementAdmin from './UserManagementAdmin';
import RoleAccessAdmin from './RoleAccessAdmin';
import Inventory from './Inventory';
import Hiring from './Hiring';
import StorePos from './StorePos';
import {
  getDefaultHomeTab,
  isSupermarketContext,
} from './entityContext';
import RestaurantDashboardLayout, {
  RESTAURANT_SIDEBAR_ITEMS,
  SUPERMARKET_SIDEBAR_ITEMS,
} from './RestaurantDashboardLayout';
import {
  getDefaultRestaurantHomeTab,
  getFilteredRestaurantSidebarItems,
  pickAllowedMenuChannel,
  pickAllowedOrderChannel,
} from './restaurantServiceAccess';
import {
  filterSidebarItemsByPermission,
  getDefaultPosTab,
  resolveAllowedAdminTab,
} from './rbacSession';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import { ROUTES } from '../constants/appRoutes';
import { PARTNER_LOGIN_PATH } from '../partner/PartnerLogin';
import { hasActiveSession } from '../utils/sessionManager';

export { RESTAURANT_SIDEBAR_ITEMS, SUPERMARKET_SIDEBAR_ITEMS };

function getSidebarItems(enabledServiceTypes = null, servicesLoading = false) {
  const base = isSupermarketContext()
    ? SUPERMARKET_SIDEBAR_ITEMS
    : getFilteredRestaurantSidebarItems(enabledServiceTypes, RESTAURANT_SIDEBAR_ITEMS, {
        servicesLoading,
      });
  return filterSidebarItemsByPermission(base);
}

function getDefaultTab(enabledServiceTypes = null) {
  const roleDefault = resolveAllowedAdminTab(null);
  const allowed = getSidebarItems(enabledServiceTypes).map(([id]) => id);
  if (allowed.includes(roleDefault)) return roleDefault;
  if (allowed.length) return allowed[0];
  if (isSupermarketContext()) return getDefaultHomeTab();
  return getDefaultRestaurantHomeTab(enabledServiceTypes, RESTAURANT_SIDEBAR_ITEMS) || getDefaultPosTab();
}

/** Tabs that use the full-height flush main panel (same as Dine-In). */
const FLUSH_LAYOUT_TABS = [
  'dine-in',
  'pos',
  'orders',
  'restaurants',
  'purchase',
  'finance',
  'inventory',
  'user-management',
  'roles',
  'hiring',
];

/** Legacy URL tab names still supported after nav changes. */
function normalizeTabFromUrl(tab, enabledServiceTypes = null) {
  if (!tab) return null;
  const t = tab.toLowerCase();
  const supermarket = isSupermarketContext();
  const validTabs = getSidebarItems(enabledServiceTypes).map(([id]) => id);

  if (supermarket) {
    if (t === 'dine-in' || t === 'pos') return 'pos';
    if (t === 'menu' || t === 'restaurants' || t === 'grocery-items') return 'inventory';
    if (t === 'payment') return 'purchase';
    return validTabs.includes(t) ? t : null;
  }

  if (t === 'payment') return 'finance';
  if (t === 'menu') return 'restaurants';
  if (t.startsWith('restaurants/')) return 'restaurants';
  return validTabs.includes(t) ? t : null;
}

function getTabFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return normalizeTabFromUrl(params.get('tab'));
}

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
    useRestaurantEnabledServices();
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = getTabFromUrl();
    const fromState = normalizeTabFromUrl(location.state?.activeTab);
    const tab = fromUrl || fromState || getDefaultTab();
    const validTabs = getSidebarItems().map(([id]) => id);
    return validTabs.includes(tab) ? tab : getDefaultTab();
  });
  const [menuChannel, setMenuChannel] = useState(
    () => getMenuChannel(new URLSearchParams(window.location.search).get('channel')).key
  );
  const [orderChannel, setOrderChannel] = useState(() => {
    const param = new URLSearchParams(window.location.search).get('orderType');
    return isSupermarketContext()
      ? getStoreOrderChannel(param).key
      : getOrderChannel(param).key;
  });

  useEffect(() => {
    if (!hasActiveSession()) {
      navigate(PARTNER_LOGIN_PATH, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const fromUrl = normalizeTabFromUrl(searchParams.get('tab'), enabledServiceTypes);
    const fromState = normalizeTabFromUrl(location.state?.activeTab, enabledServiceTypes);
    const requested = fromUrl || fromState;
    const allowedTab = resolveAllowedAdminTab(requested);
    const validTabs = getSidebarItems(enabledServiceTypes, servicesLoading).map(([id]) => id);
    const nextTab = validTabs.includes(allowedTab)
      ? allowedTab
      : getDefaultTab(enabledServiceTypes);

    setActiveTab(nextTab);

    const urlTab = searchParams.get('tab');
    // Nested menu URLs (templates, food item, recipe) normalize to sidebar "restaurants"
    // but must keep their path so SupervisorRestaurant can open the right sub-view.
    const isNestedRestaurantsTab =
      nextTab === 'restaurants' &&
      typeof urlTab === 'string' &&
      urlTab.toLowerCase().startsWith('restaurants/');
    if (urlTab && nextTab && urlTab !== nextTab && !isNestedRestaurantsTab) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', nextTab);
      navigate(`${ROUTES.businessAdmin}?${params.toString()}`, { replace: true });
    }

    const channelParam = searchParams.get('channel');
    if (channelParam && !isSupermarketContext() && !servicesLoading) {
      const allowed = pickAllowedMenuChannel(
        getMenuChannel(channelParam).key,
        enabledServiceTypes,
        { servicesLoading }
      );
      if (allowed) setMenuChannel(allowed);
    }

    const orderTypeParam = searchParams.get('orderType');
    if (orderTypeParam && !servicesLoading) {
      if (isSupermarketContext()) {
        setOrderChannel(getStoreOrderChannel(orderTypeParam).key);
      } else {
        const allowed = pickAllowedOrderChannel(orderTypeParam, enabledServiceTypes, {
          servicesLoading,
        });
        if (allowed) setOrderChannel(allowed);
      }
    }
  }, [searchParams, location.state?.activeTab, enabledServiceTypes, servicesLoading, navigate]);

  useEffect(() => {
    if (isSupermarketContext() || servicesLoading) return;

    const allowedMenu = pickAllowedMenuChannel(menuChannel, enabledServiceTypes, { servicesLoading });
    if (allowedMenu && allowedMenu !== menuChannel) {
      setMenuChannel(allowedMenu);
    }

    const allowedOrder = pickAllowedOrderChannel(orderChannel, enabledServiceTypes, { servicesLoading });
    if (allowedOrder && allowedOrder !== orderChannel) {
      setOrderChannel(allowedOrder);
    }
  }, [enabledServiceTypes, menuChannel, orderChannel, servicesLoading]);

  const handleSelectMenuChannel = (key) => {
    const channelKey =
      pickAllowedMenuChannel(getMenuChannel(key).key, enabledServiceTypes, { servicesLoading }) ||
      getMenuChannel(key).key;
    setMenuChannel(channelKey);
    navigate(`${ROUTES.businessAdmin}?tab=restaurants&channel=${channelKey}`);
  };

  const handleSelectOrderChannel = (key) => {
    const channelKey = isSupermarketContext()
      ? getStoreOrderChannel(key).key
      : pickAllowedOrderChannel(getOrderChannel(key).key, enabledServiceTypes, { servicesLoading }) ||
        getOrderChannel(key).key;
    setOrderChannel(channelKey);
    navigate(`${ROUTES.businessAdmin}?tab=orders&orderType=${channelKey}`);
  };

  const renderTabContent = () => {
    if (isSupermarketContext()) {
      switch (activeTab) {
        case 'pos':
          return <StorePos />;
        case 'orders':
          return <Order />;
        case 'purchase':
          return <StorePurchase />;
        case 'inventory':
          return <Inventory />;
        case 'user-management':
          return <UserManagementAdmin />;
        case 'roles':
          return <RoleAccessAdmin />;
        case 'hiring':
          return <Hiring />;
        default:
          return null;
      }
    }

    switch (activeTab) {
      case 'dine-in': {
        const posOrderType =
          searchParams.get('posOrderType') ??
          location.state?.orderTypeFilter ??
          location.state?.orderType ??
          'offline';
        return <PlaceOrder embedded orderTypeFilter={posOrderType} />;
      }
      case 'orders': {
        const editingCatering =
          location.state?.editOrderId != null &&
          (location.state?.orderTypeFilter === 'catering' ||
            location.state?.orderType === 'catering' ||
            searchParams.get('orderType') === 'catering');
        if (editingCatering) {
          return <PlaceOrder embedded orderTypeFilter="catering" />;
        }
        return (
          <Order
            enabledServiceTypes={enabledServiceTypes}
            servicesLoading={servicesLoading}
          />
        );
      }
      case 'restaurants':
        return <SupRestaurant menuChannel={menuChannel} />;
      case 'purchase':
        return <Purchase />;
      case 'finance':
        return <Finance />;
      case 'inventory':
        return <Inventory />;
      case 'user-management':
        return <UserManagementAdmin />;
      case 'roles':
        return <RoleAccessAdmin />;
      case 'hiring':
        return <Hiring />;
      default:
        return null;
    }
  };

  const isFlushLayout = FLUSH_LAYOUT_TABS.includes(activeTab);

  return (
    <RestaurantDashboardLayout
      activeTab={activeTab}
      flush={isFlushLayout}
      menuChannel={menuChannel}
      onSelectMenuChannel={handleSelectMenuChannel}
      orderChannel={orderChannel}
      onSelectOrderChannel={handleSelectOrderChannel}
      enabledServiceTypes={enabledServiceTypes}
      servicesLoading={servicesLoading}
    >
      {renderTabContent()}
    </RestaurantDashboardLayout>
  );
};

export default Home;
